/**
 * Pure formatters for the operator station view (WS-F2). They live in their own module,
 * away from `station.tsx`, for two reasons: the component file then exports only components
 * (react-refresh), and — the one that matters — the rule that an unknown value renders "—"
 * and never 0 is testable on its own, without rendering anything.
 */

import type { PlantNode } from "@foundry/foundry-plant";
import type { StationCoverageRow } from "@/lib/api/stations";
import type { StationReading, StationReadingSource, StationView } from "@/lib/api/twin-station";
import type { PlantTime } from "@/lib/shift-calendar";
import { shownFor, typedToStored } from "@/lib/units/display";
import type { DisplayPrefs } from "@/lib/units/prefs";

/** The em-dash every withheld / unknown value renders as. Never "0". */
export const WITHHELD = "—";

/**
 * The station view's address for one node of the plant. It takes the node, not its entity definition: the station
 * reads the equipment by the node's id, while a definition id (ent-pour) is shared by every node built from the same
 * template, so a link built from it found no equipment, or another node's.
 */
export function stationViewHref(node: PlantNode): string {
  return `/app/twin/station/${encodeURIComponent(node.id)}`;
}

/**
 * The machines the station picker offers (Floor capture's Station tab, the Navigation review, slice (c)): the plant's
 * nodes in name order, those whose name or one-line description holds every word of `find` (any case).
 */
export function stationChoicesOf(nodes: readonly PlantNode[], find: string): PlantNode[] {
  const words = find.toLowerCase().split(/\s+/).filter(Boolean);
  return nodes
    .filter((n) => {
      const text = `${n.instanceName} ${n.entity.oneLine}`.toLowerCase();
      return words.every((w) => text.includes(w));
    })
    .sort((a, b) => a.instanceName.localeCompare(b.instanceName));
}

/**
 * The picker's machines with data first (sweep PR1): a machine whose station coverage has data or a latest day comes
 * before one with none, each group in the order given (name order, stationChoicesOf). With the coverage unknown
 * (null) the order is kept.
 */
export function stationChoicesByData(
  nodes: readonly PlantNode[],
  coverage: ReadonlyMap<string, StationCoverageRow> | null,
): PlantNode[] {
  if (!coverage) return [...nodes];
  const hasData = (n: PlantNode) => {
    const row = coverage.get(n.id);
    return !!row && (row.has_data || !!row.latest_day);
  };
  return [...nodes.filter(hasData), ...nodes.filter((n) => !hasData(n))];
}

/** Format a number for display. `null`/non-finite ⇒ null, and the caller renders "—". */
export function fmt(value: number | null | undefined, digits = 2): string | null {
  if (value === null || value === undefined || !Number.isFinite(value)) return null;
  return value.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  });
}

/**
 * A reading's displayed value: the number, else the text, else "—".
 *
 * This is the SINGLE place a station value becomes text, which is exactly where a `?? 0`
 * would be written by somebody tidying up a nullable. It is unit-tested for that.
 */
export function readingDisplay(r: StationReading, prefs?: DisplayPrefs): string {
  // A value in a unit the org's choice converts is shown converted, with the unit it is now in (units slice 2b).
  const s = shownFor(r.unit ?? "", prefs);
  const n = fmt(r.value === null ? null : s.convert(r.value), 2);
  if (n !== null) return s.unit ? `${n} ${s.unit}` : n;
  if (r.value_text) return r.value_text;
  return WITHHELD;
}

/**
 * The line under the one reading of a port fed by several sources (one series per port, S5): the source its value is
 * read from, "From Muller history."; null for every other reading, which reads as before.
 */
export function readingFromText(r: StationReading): string | null {
  const from = r.from?.trim();
  return from ? `From ${from}.` : null;
}

/**
 * A line for one of the port's other sources, under its one reading: its own latest reading in its own unit (shown in
 * the org's units, as every value here) and when, or that it has none in the look-back, or why the port never reads it.
 */
export function otherSourceText(src: StationReadingSource, prefs?: DisplayPrefs, zone?: string): string {
  const name = src.name.trim() || "Another source";
  if (!src.read) return `${name} is never read.${src.why ? ` ${src.why}` : ""}`;
  const value = src.value ?? null;
  if (value === null && !src.value_text) return `${name} also feeds it, with no reading in the last 90 days.`;
  const shown = readingDisplay(
    { port: "", tag_id: src.tag_id, tag_name: "", source: "", tier: "UNKNOWN", basis: "", unit: src.unit, value, value_text: src.value_text },
    prefs,
  );
  return src.observed_at ? `${name} also recorded ${shown}, at ${shortTime(src.observed_at, zone)}.` : `${name} also recorded ${shown}.`;
}

/**
 * The zone the station reads its times in: the plant's, when the browser knows it, else undefined and the browser's
 * own. A shop-floor time read in the viewer's zone put a heat's start hours off for anyone not at the plant.
 */
export function stationZone(pt: PlantTime | null | undefined): string | undefined {
  const tz = pt?.timezone?.trim();
  if (!tz) return undefined;
  try {
    new Intl.DateTimeFormat("en-GB", { timeZone: tz });
    return tz;
  } catch {
    return undefined;
  }
}

/** Short time for a shop-floor screen, in `zone` (the plant's) when given; the full ISO stays in the title attribute. */
export function shortTime(iso: string | undefined, zone?: string): string {
  if (!iso) return WITHHELD;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return WITHHELD;
  const opts: Intl.DateTimeFormatOptions = { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" };
  try {
    return d.toLocaleString(undefined, zone ? { ...opts, timeZone: zone } : opts);
  } catch {
    return d.toLocaleString(undefined, opts); // a zone the browser does not know
  }
}

/**
 * How old a reading is at `now` (epoch ms): "just now", "12 min ago", "5 h ago", "1 day ago", "41 days ago". The station shows
 * each tag's latest reading from a 90-day look-back, so a value weeks old must say so rather than sit under a heading
 * that reads as live. null when either time is missing or does not read.
 */
export function ageText(iso: string | undefined, now: number | undefined): string | null {
  if (!iso || now === undefined || !Number.isFinite(now)) return null;
  const at = Date.parse(iso);
  if (!Number.isFinite(at)) return null;
  const minutes = Math.floor((now - at) / 60_000);
  if (minutes < -5) return "timed ahead of this clock";
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  // Days from a full day on, so two readings a few hours apart near the boundary read in one unit ("1 day ago",
  // "2 days ago"), where "35 h ago" used to sit beside "2 days ago".
  if (hours < 24) return `${hours} h ago`;
  const days = Math.floor(hours / 24);
  return days === 1 ? "1 day ago" : `${days} days ago`;
}

/**
 * A CONTROL slot's displayed value (WS-F2b): the number, else "—".
 *
 * Deliberately its own function rather than a reuse of `readingDisplay`. A control and a
 * reading are different claims — one is what the machine was TOLD, the other is what it
 * REPORTED — and a shared formatter is how they would eventually come to look the same.
 * The rule is identical and the reason for it is not: null here means NOBODY SET IT, and
 * a "0" would tell an operator the furnace is dialled to zero.
 */
export function controlDisplay(c: { value: number | null; unit?: string }, prefs?: DisplayPrefs): string {
  const s = shownFor(c.unit ?? "", prefs);
  const n = fmt(c.value === null ? null : s.convert(c.value), 2);
  if (n === null) return WITHHELD;
  return s.unit ? `${n} ${s.unit}` : n;
}

/**
 * A target's words (units slice 2b): its point or its window, in the org's units, and the unit they are in. The point
 * is its value to two places (three for a share), the window its ends to three: as it always read, in the shown unit.
 */
export function targetTexts(
  t: { value: number | null; lo?: number | null; hi?: number | null; unit?: string },
  prefs?: DisplayPrefs,
): { point: string | null; window: string | null; unit: string } {
  const s = shownFor(t.unit ?? "", prefs);
  const to = (v: number | null | undefined) => (v === null || v === undefined ? v : s.convert(v));
  const window =
    t.lo !== null && t.lo !== undefined && t.hi !== null && t.hi !== undefined
      ? `${fmt(to(t.lo), 3)} to ${fmt(to(t.hi), 3)}`
      : null;
  return { point: fmt(to(t.value), t.unit === "%" ? 3 : 2), window, unit: s.unit };
}

/**
 * A reading's usual range ("range 1,400 to 1,500") in the org's units, its ends joined by "to", not a dash. A range in
 * the unit it was set in reads as it always did (two places). A range read in another unit is rounded, each end to its
 * own size (rangeDigits), so a converted band prints no false precision ("17,636.98 to 41,887.83" reads "17,637 to
 * 41,888") and a wide one keeps its small end ("0.794 to 1,499", never "1 to 1,499").
 */
export function rangeDisplay(r: { min: number; max: number; unit?: string }, prefs?: DisplayPrefs): string {
  const s = shownFor(r.unit ?? "", prefs);
  const lo = s.convert(r.min);
  const hi = s.convert(r.max);
  const converted = lo !== r.min || hi !== r.max;
  const digits = (v: number) => (converted ? rangeDigits(v) : 2);
  return `range ${fmt(lo, digits(lo))} to ${fmt(hi, digits(hi))}`;
}

/**
 * The decimals a converted range's end is shown to: about three significant figures of it, never more than four
 * decimals and never fewer than none (1,500 takes 0, 66.1 takes 1, 4.8 takes 2, 0.45 takes 3, 0 takes 0).
 */
export function rangeDigits(v: number): number {
  const size = Math.abs(v);
  if (!Number.isFinite(size) || size === 0) return 0;
  return Math.min(4, Math.max(0, 2 - Math.floor(Math.log10(size))));
}

/** The unit an entry field is read and typed in (the org's shown one), and what a typed value is in the stored unit. */
export function entryValueIn(
  typed: number,
  storedUnit: string,
  prefs: DisplayPrefs | undefined,
): number {
  if (!prefs) return typed;
  return typedToStored(typed, shownFor(storedUnit, prefs).unit, storedUnit) ?? typed;
}

/**
 * Short label for one operator entry in the history list. A SETPOINT and an OBSERVATION
 * must not be able to render identically — that is the distinction the whole write half
 * carries — so the kind is spelled out rather than implied by which field is populated.
 * The measured value is named by its reading's label (`labelOf`, by the tag's name), else its name in words.
 */
export function entryLabel(
  e: {
    kind: string;
    metric?: string;
    target_key?: string;
  },
  labelOf?: (metric: string) => string | undefined,
): string {
  if (e.kind === "setpoint") return `Set ${targetLabel(e.target_key ?? "")}`;
  const metric = e.metric ?? "";
  const name = (metric && labelOf?.(metric)) || nameWords(metric);
  return `Measured ${name}`.trimEnd();
}

/**
 * A name in words, for a reading the server sent no label for: "first_car" reads "First car", "line.speed" reads
 * "Line speed". The server's label (entityPortLabel, internal/twin/entity_port_label.go) is the one to show; this is
 * only the fallback.
 */
export function nameWords(name: string): string {
  const words = name
    .split(/[_.\s-]+/)
    .filter(Boolean)
    .join(" ");
  return words.replace(/^./, (c) => c.toUpperCase());
}

/** A reading's name on the page: its label, else its port in words, else its tag's name in words. */
export function readingLabel(r: { label?: string; port?: string; tag_name?: string }): string {
  return r.label?.trim() || nameWords(r.port || r.tag_name || "");
}

/** Whether a reading has a value (a number or a text) in the look-back. */
export function hasValue(r: StationReading): boolean {
  return (r.value !== null && r.value !== undefined) || !!r.value_text;
}

/**
 * The shown ports with no reading in the look-back, folded into one line by their names, joined by semicolons because
 * a name may hold a comma ("Pour temperature, first pour ladle"): "No reading yet for: Pour temperature, first pour
 * ladle; First mould car." null when every port has one.
 */
export function emptyReadingsLine(readings: readonly StationReading[]): string | null {
  const names = readings.filter((r) => !hasValue(r)).map(readingLabel);
  if (names.length === 0) return null;
  return `No reading yet for: ${names.join("; ")}.`;
}

/**
 * A day on the plant's clock ("9 Oct 2026"), in `zone` when given; "" for a time that does not read. The month is
 * named from the one table the picker's badge uses too (en-GB itself writes "Sept").
 */
export function plantDayText(iso: string | undefined, zone?: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const opts: Intl.DateTimeFormatOptions = { day: "numeric", month: "numeric", year: "numeric" };
  let parts: Intl.DateTimeFormatPart[];
  try {
    parts = new Intl.DateTimeFormat("en-GB", zone ? { ...opts, timeZone: zone } : opts).formatToParts(d);
  } catch {
    parts = new Intl.DateTimeFormat("en-GB", opts).formatToParts(d); // a zone the browser does not know
  }
  const part = (type: string) => Number(parts.find((p) => p.type === type)?.value);
  const month = part("month") - 1;
  if (!(month >= 0 && month <= 11)) return "";
  return `${part("day")} ${SHORT_MONTHS[month]} ${part("year")}`;
}

/**
 * The sentence at the top of a machine with no reading in the look-back: nothing ever, or nothing lately and the day
 * the last one came (the server's `last_reading_at`, from the tag's profile).
 */
export function nothingRecordedText(lastReadingAt: string | undefined, zone?: string): string {
  const day = plantDayText(lastReadingAt, zone);
  if (!day) return "Nothing has been recorded for this machine yet.";
  return `Nothing has been recorded for this machine in the last 90 days. Its last reading was on ${day}.`;
}

/**
 * When a reading was entered, beside when it was taken: only for a value a person or a handheld entered later. A file
 * the plant sent was "entered" when the platform read the file, which a person reads as a second event, so it says
 * nothing; nor does an entry timed when it was made.
 */
export function enteredText(r: StationReading, zone?: string): string {
  if (!r.entered_at || r.data_class === "imported") return "";
  if (r.observed_at && Date.parse(r.observed_at) === Date.parse(r.entered_at)) return "";
  return ` · entered ${shortTime(r.entered_at, zone)}`;
}

/** An event's kind in words ("charge_added" reads "Charge added"). */
export function eventKindWords(kind: string): string {
  return nameWords(kind);
}

/** What set an event off, in words: the four triggers heat_events records (migration 0108), else the name in words. */
export function eventTriggerWords(source: string): string {
  switch (source) {
    case "plc_register":
      return "From the machine's controller";
    case "app_button":
      return "Entered in the app";
    case "ble_frame":
      return "From a handheld probe";
    case "derived_clock":
      return "Worked out from the clock";
    default:
      return nameWords(source);
  }
}

const SHORT_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * The picker's badge for a machine (the station coverage): "Last reading 9 Oct" from its latest plant day (the plant
 * day the server already counts in the plant's time zone; the year is named when it is not `thisYear`), else "No data
 * yet".
 */
export function lastReadingBadge(latestDay: string | null | undefined, thisYear: number): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(latestDay ?? "");
  const month = m ? Number(m[2]) - 1 : -1;
  if (!m || month < 0 || month > 11) return "No data yet";
  const day = `${Number(m[3])} ${SHORT_MONTHS[month]}`;
  return Number(m[1]) === thisYear ? `Last reading ${day}` : `Last reading ${day} ${m[1]}`;
}

/** Operator-facing label for a target key (mirrors RecipeSetpointsPanel's wording). */
export function targetLabel(key: string): string {
  switch (key) {
    case "target_charge_kg":
      return "Charge";
    case "target_tap_temp_c":
      return "Tap temperature";
    case "target_designed_weight_kg":
      return "Designed weight per casting";
    case "target_pour_temp_c":
      return "Pour temperature";
    case "target_mg_resid_pct":
      return "Mg residual";
    case "target_compactibility_pct":
      return "Compactibility";
    default:
      return key
        .replace(/^target_/, "")
        .replace(/_(kg|pct|c)$/, "")
        .replace(/_/g, " ")
        .replace(/^./, (c) => c.toUpperCase());
  }
}

/** The machine page's subtitle: the heat line only where the machine opens heats (the server's `heat.can_open`). */
export function stationSubtitle(station: StationView | undefined): string {
  return station?.heat.can_open
    ? "One machine, one heat: what it aims at, what it reads, and what has happened here."
    : "What this machine reads, and what has happened here.";
}
